import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, UserCheck } from 'lucide-react';
import SEO from '../components/SEO';
import ScrollIndicator from '../components/ScrollIndicator';
import './LegalPages.css';

const sections = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'information-we-collect', label: 'Information We Collect' },
  { id: 'how-we-use', label: 'How We Use Your Information' },
  { id: 'confidentiality', label: 'Confidentiality and Security' },
  { id: 'electronic-systems', label: 'Electronic Systems' },
  { id: 'dictation-devices', label: 'Dictation Devices' },
  { id: 'data-flow', label: 'Data Flow and Storage' },
  { id: 'ai-tools', label: 'AI Tools' },
  { id: 'information-sharing', label: 'Information Sharing' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'telehealth-privacy', label: 'Telehealth Privacy' },
  { id: 'data-breach', label: 'Data Breach Notification' },
  { id: 'cultural-safety', label: 'Cultural Safety' },
  { id: 'changes', label: 'Changes to This Policy' },
  { id: 'contact', label: 'Contact Us' },
];

const PrivacyPolicy = () => {
  return (
    <div className="page-legal" data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy | Clinical Minds Psychology"
        description="How Clinical Minds Psychology collects, uses, stores and protects your personal information under the Privacy Act 1988 and the Australian Privacy Principles."
        canonicalUrl="https://www.clinicalminds.net/privacy-policy"
        noindex={false}
      />
      <ScrollIndicator />

      <section id="main-content" className="legal-hero-compact">
        <div className="container">
          <span className="section-label">Privacy</span>
          <h1 className="heading-hero">Privacy Policy</h1>
          <p className="legal-subline">Clinical Minds Psychology | ABN 96 150 581 605 | Effective July 2026</p>
        </div>
      </section>

      <section className="legal-content">
        <div className="container">
          <div className="legal-text">

            {/* 3 Key Points */}
            <div className="legal-key-points" data-testid="privacy-key-points">
              <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Your Privacy in 3 Key Points</h2>
              <div className="legal-cards-grid">
                <div className="legal-card card">
                  <div className="legal-card-icon"><Shield size={28} strokeWidth={1.5} /></div>
                  <h3 className="heading-3">Australian Data Sovereignty</h3>
                  <p className="body-medium">Your information is stored exclusively in Microsoft's Sydney data centre and never leaves Australia.</p>
                </div>
                <div className="legal-card card">
                  <div className="legal-card-icon"><Lock size={28} strokeWidth={1.5} /></div>
                  <h3 className="heading-3">Bank-Level Security</h3>
                  <p className="body-medium">Your data is encrypted in transit (TLS 1.2+) and at rest (AES-256), with multiple layers of protection including physical security, access controls, and legal safeguards.</p>
                </div>
                <div className="legal-card card">
                  <div className="legal-card-icon"><UserCheck size={28} strokeWidth={1.5} /></div>
                  <h3 className="heading-3">You're in Control</h3>
                  <p className="body-medium">You can access, correct, or request deletion of your information at any time. We never sell or share your data without your permission.</p>
                </div>
              </div>
            </div>

            {/* Table of Contents */}
            <nav className="legal-toc" data-testid="privacy-toc">
              <h3 className="heading-3">Contents</h3>
              <ol className="legal-toc-list">
                {sections.map((s) => (
                  <li key={s.id}><a href={`#${s.id}`}>{s.label}</a></li>
                ))}
              </ol>
            </nav>

            {/* Sections */}
            <div id="introduction" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Introduction</h2>
              <p className="body-medium">
                Clinical Minds Psychology ("we", "us", "our") is committed to protecting your privacy and the confidentiality of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information in accordance with the Privacy Act 1988 (Cth), the Australian Privacy Principles (APPs), and the Psychology Board of Australia Code of Conduct (2025).
              </p>
            </div>

            <div id="information-we-collect" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Information We Collect</h2>
              <p className="body-medium">
                We collect only personal information that is necessary to provide you with professional psychology services, including:
              </p>
              <ul className="legal-list">
                <li>Personal details (name, date of birth, contact information)</li>
                <li>Health information relevant to your psychological care</li>
                <li>Medicare and health insurance details</li>
                <li>Referral information from your GP or other healthcare providers</li>
                <li>Session notes and treatment records</li>
                <li>Payment and billing information</li>
              </ul>
              <p className="body-medium">
                Where you provide card details, your full card number is held securely by our payment provider, not by Clinical Minds.
              </p>
              <p className="body-medium">
                Information may be collected directly from you, or from third parties (such as your GP or referrer) with your knowledge and consent. We will ask you for this consent prior to collecting any information and will explain the consent process to you in person at your first appointment. This information is collected and used solely to provide you with professional psychology services. You may choose not to provide certain information; however, this may impact our ability to provide services.
              </p>
              <p className="body-medium">
                Consent to collect, store, and use your information is an ongoing process. We will discuss and review your consent annually, ensuring you understand what it means and seeking your agreement for another year. You may review or withdraw your consent at any time. Refusal or withdrawal of consent will not affect your access to care, except where specific information is essential for service delivery or required by law (such as mandatory reporting obligations).
              </p>
            </div>

            <div id="how-we-use" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">How We Use Your Information</h2>
              <p className="body-medium">Your personal information is used solely for:</p>
              <ul className="legal-list">
                <li>Providing psychological assessment and treatment services</li>
                <li>Communicating with you about your appointments and care</li>
                <li>Processing Medicare claims (including bulk billed services where you assign your Medicare benefit to Clinical Minds) and insurance payments</li>
                <li>Maintaining accurate clinical records as required by professional standards</li>
                <li>Complying with legal and professional obligations</li>
              </ul>
              <p className="body-medium">
                Your information will not be used for direct marketing purposes. De-identified information may be used for professional supervision, audit, or practice improvement only with your consent, in accordance with the Psychology Board of Australia Code of Conduct (2025).
              </p>
            </div>

            <div id="confidentiality" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Confidentiality and Security</h2>
              <p className="body-medium">
                As a registered psychologist, Lachlan Beaton is bound by strict confidentiality requirements under:
              </p>
              <ul className="legal-list">
                <li>Psychology Board of Australia Code of Conduct (2025)</li>
                <li>Australian Psychological Society Code of Ethics</li>
                <li>Australian Privacy Principles</li>
                <li>Health Records legislation</li>
              </ul>
              <p className="body-medium">Your information may be disclosed where required by law, including:</p>
              <ul className="legal-list">
                <li>Mandatory reporting of child abuse or neglect</li>
                <li>If you or someone else is at serious risk of harm</li>
                <li>Court orders or subpoenas</li>
              </ul>
              <p className="body-medium">
                We use a combination of physical (locked storage), administrative (access controls), and technical (encryption, passwords) safeguards to protect your personal information against unauthorised access, alteration, disclosure, or destruction. Both electronic and paper records are stored securely and are accessible only to authorised personnel.
              </p>
            </div>

            <div id="electronic-systems" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Use of Electronic Systems and Third-Party Platforms</h2>
              <p className="body-medium">
                Clinical Minds Psychology utilises secure, industry-standard electronic platforms to collect, store, and manage client information. These systems are selected for their compliance with Australian privacy legislation and professional standards.
              </p>
              <p className="body-medium">
                Client contact details may be collected using forms powered by Microsoft and Microsoft Azure. All data entered via Microsoft Forms is stored securely on servers located in Australia and protected by Microsoft's enterprise security protocols. Some client files, with your consent, may be stored within a Microsoft Business 365 OneDrive account. Microsoft 365 employs strong encryption, access controls, and complies with the Privacy Act 1988 (Cth) and the APPs.
              </p>
              <p className="body-medium">
                Appointment scheduling, clinical notes, and additional client records are managed using Zanda (formerly Power Diary), a practice management system designed for healthcare professionals. Zanda stores data on secure servers in Australia, is compliant with relevant privacy legislation, and implements robust security measures. Telehealth sessions are conducted using Zoom, a secure video platform with end-to-end encryption, compliant with Australian privacy standards.
              </p>
              <p className="body-medium">
                Payment processing is handled by our secure payment provider, which stores your card details on our behalf; Clinical Minds does not hold your full card number.
              </p>
              <p className="body-medium">
                Access to all electronic records is restricted to authorised personnel only. All platforms used employ encryption, password protection, and regular security audits. Data is not transferred or stored outside Australia unless required by law and with your explicit consent, and only to jurisdictions with equivalent data protection standards. You will be notified if this occurs.
              </p>
              <p className="body-medium">
                Clinical Minds Psychology has taken reasonable steps to ensure that these providers adhere to Australian privacy laws and the Psychology Board of Australia Code of Conduct (2025). All third-party providers are bound by contractual obligations to protect your personal information and maintain confidentiality. You have the right to request information about how your data is stored and managed on these platforms, and to request access to, correction of, or deletion of your personal information held in any of these systems, subject to legal and professional requirements.
              </p>
            </div>

            <div id="dictation-devices" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Use of Dictation Devices</h2>
              <p className="body-medium">
                Clinical Minds Psychology utilises a dictation device for clinical note-taking. Transcripts are temporarily stored on the device, which employs AES-256 encryption to protect data both at rest and during transfer. No transcripts are stored in cloud services; instead, all transcripts are securely exported to our encrypted Microsoft server. Transcripts are systematically deleted from the device and the server within 24 hours of creation. This workflow ensures that your information is protected through industry-standard encryption at all stages and is only retained within secure, Australian-based storage systems.
              </p>
            </div>

            <div id="data-flow" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Data Flow and Storage Locations</h2>
              <p className="body-medium">
                When you submit information or documents through our website forms, your data is processed and securely stored within Microsoft Azure cloud services located in Australia (Sydney, New South Wales). This includes Azure Static Web Apps (website hosting), Azure Function Apps (form processing), Azure Cosmos DB (database storage), and Azure Blob Storage (document storage). Some client files and documents may also be stored in Microsoft 365 Business OneDrive, which is hosted in Australian Azure data centres.
              </p>
              <p className="body-medium">
                Clinical notes, appointments, and client records are managed using Zanda (formerly Power Diary), which stores data securely in Australia, outside Azure but with equivalent privacy protections. Telehealth sessions are conducted via Zoom, which uses end-to-end encryption and complies with Australian privacy standards. No session recordings or personal data from Zoom are stored unless specifically documented in your clinical record.
              </p>
              <p className="body-medium">
                All systems operate independently, and your data is never transferred between them without your explicit consent. All personal information and documents remain in Australia and are not transferred overseas unless required by law or with your written consent. Our data retention policy is available on request.
              </p>
            </div>

            <div id="ai-tools" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Use of AI Tools in Psychological Services</h2>
              <p className="body-medium">
                As part of our commitment to high-quality care, Clinical Minds Psychology may use secure, healthcare-specific artificial intelligence (AI) tools, such as BastionGPT, to assist with clinical documentation, information retrieval, and report generation. AI tools may be used to:
              </p>
              <ul className="legal-list">
                <li>Draft and organise clinical notes, treatment plans, or reports</li>
                <li>Summarise session transcripts or key details to support your care</li>
                <li>Retrieve evidence-based information for your psychologist's reference</li>
              </ul>
              <p className="body-medium">
                AI tools support documentation and do not replace professional judgment or decision-making. Any information processed by AI tools is handled securely, in compliance with Australian privacy laws and professional standards. Data is not stored, retained, or used for any purpose beyond your specific session, and is not shared with third parties or used for marketing.
              </p>
              <p className="body-medium">
                Your consent will be requested before using AI tools with your information. You may decline or withdraw your consent at any time without affecting your care, and you are encouraged to ask questions about how these tools work and how your data is handled. For further details, see our Privacy Policy Addendum: Use of AI-Assisted Tools in Psychological Services.
              </p>
            </div>

            <div id="information-sharing" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Information Sharing</h2>
              <p className="body-medium">Your personal information will only be shared in the following circumstances:</p>
              <ul className="legal-list">
                <li>With your written consent</li>
                <li>When required by law (e.g. court orders, mandatory reporting)</li>
                <li>In emergency situations where there is serious risk to the health or safety of you or others</li>
                <li>For Medicare claims processing (including assignment of benefit for bulk billed services) and health insurance purposes</li>
                <li>With other healthcare providers involved in your care, with your consent</li>
              </ul>
              <p className="body-medium">
                For clients under 18, information may be shared with parents or guardians in accordance with legal and ethical obligations. Whether a young person consents to their own treatment is based on their maturity and understanding rather than a fixed age; as a general guide, young people from around 14 years who are assessed as able to understand the nature and consequences of their care may provide their own consent. If disclosure is deemed not in the minor's best interests, information may be withheld in accordance with professional standards and legal requirements.
              </p>
              <p className="body-medium">
                De-identified data will only be used for supervision, audit, or research with your consent.
              </p>
            </div>

            <div id="your-rights" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Your Rights</h2>
              <p className="body-medium">Under Australian privacy law, you have the right to:</p>
              <ul className="legal-list">
                <li>Access your personal information held by us</li>
                <li>Request correction of inaccurate or incomplete information</li>
                <li>Request deletion of your personal information (subject to legal requirements)</li>
                <li>Receive a copy of your clinical records</li>
                <li>Make a complaint about how your privacy has been handled</li>
              </ul>
              <p className="body-medium">
                You will not be penalised or disadvantaged for exercising your privacy rights or making a complaint. If you require this policy in an alternative format (e.g. large print, translated), please let us know. To access or correct your information, please contact us in writing. Requests will be processed within 30 days. In rare cases, access may be refused as permitted by law (e.g. if it poses a serious threat to health or safety).
              </p>
            </div>

            <div id="data-retention" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Data Retention</h2>
              <p className="body-medium">
                Clinical records are retained for a minimum of seven (7) years after your last appointment, or until age 25 for clients under 18, in accordance with Psychology Board of Australia requirements. Records are securely destroyed when no longer required. Records may be retained longer if required by law or for ongoing care purposes.
              </p>
            </div>

            <div id="telehealth-privacy" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Telehealth Privacy</h2>
              <p className="body-medium">
                For telehealth sessions, we use secure, encrypted video platforms (Zoom) that comply with Australian privacy standards. Sessions are not recorded unless specifically agreed upon for therapeutic purposes. You are responsible for ensuring your location is private during telehealth sessions.
              </p>
            </div>

            <div id="data-breach" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Data Breach Notification</h2>
              <p className="body-medium">
                In the event of a data breach involving your personal information, including any third-party platform, we will comply with the Notifiable Data Breaches scheme and notify you and the Office of the Australian Information Commissioner (OAIC) as required.
              </p>
            </div>

            <div id="cultural-safety" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Cultural Safety, Inclusivity, and Non-Discrimination</h2>
              <p className="body-medium">
                Clinical Minds Psychology is committed to providing culturally safe, trauma-informed, gender-affirming, inclusive, and non-discriminatory psychological services. We respect and support clients from all backgrounds, including Aboriginal and Torres Strait Islander peoples, culturally and linguistically diverse communities, LGBTIQA+ individuals, and people living with disability. Please let us know if you have any cultural, religious, or personal preferences relevant to your care, or if you require specific support to feel safe and respected during your engagement with our service.
              </p>
            </div>

            <div id="changes" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Changes to This Policy</h2>
              <p className="body-medium">
                We may update this Privacy Policy from time to time. Any changes will be posted on our website with an updated effective date. Significant changes will be communicated to you directly.
              </p>
            </div>

            <div id="contact" className="legal-section">
              <span className="section-label">Privacy</span>
              <h2 className="heading-2">Contact Us</h2>
              <p className="body-medium">
                If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact:
              </p>
              <div className="legal-contact-box">
                <p className="body-medium"><strong>Clinical Minds Psychology</strong></p>
                <p className="body-medium">Lachlan Beaton, Clinical Psychologist (Registrar)</p>
                <p className="body-medium">AHPRA Registration: PSY0002785698</p>
                <p className="body-medium">Phone: <a href="tel:0266002710">02 6600 2710</a> | SMS: <a href="sms:0415220944">0415 220 944</a></p>
                <p className="body-medium">Email: <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a></p>
                <p className="body-medium">Address: 76 Albany Street, Coffs Harbour NSW 2450</p>
              </div>
              <p className="body-medium" style={{ marginTop: '1.5rem' }}>
                <strong>Privacy Complaints:</strong> if you believe your privacy has been breached, you can contact the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a>. You may also contact the Psychology Board of Australia or the NSW Health Care Complaints Commission if you have concerns about how your information is handled.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
